import {
  CornerInfoWithProductInfo,
  SelectedItemWithFullRecord,
} from '../interfaces/ProductMatchingSummary';
import {
  isEqToMasterSku,
  isEqToProductId,
} from '../utils/useEquivalentProductMatching.utils';

import { selectSortedEdges } from '~/api/easy-estimator-api/edges-api/useEdges';
import { DEFAULT_CORNER_RADIUS } from '~/core/constants';
import { EditableLabel } from '~/core/engine/EditableLabel';
import {
  CommonProductModel,
  CornerInfo,
  CornerRadiusModel,
  CutoutModel,
  DrawingShape,
  EdgeModel,
  PlumbingProduct,
  TearOutModel,
  getCornerRadiusFromSize,
} from '~/core/models';
import { OptionalItem } from '~/core/models/optional-items-response';
import { cloneCornerInfo, formatCornerRadiusLabel } from '~/core/utils';

/**
 * I product item like EdgeModel
 * R Raw catalog item
 * N Design item like DrawingShape
 */
export type CheckerContext<R> = {
  newCatalog: R[];
};

export type BuilderCommonArgs<I, R, N> = {
  oldValue: N[];
  item: I;
  index: number;
  ctx: CheckerContext<R>;
};

/**
 * Defines a set of strategies for handling product equivalence checks and transformations
 * when migrating or updating catalog items between product catalogs.
 *
 * @template SelectedItemModel The type representing an individual item or product.
 * @template CatalogProductModel The type representing the raw catalog item used during checking.
 * @template N The type representing the new design item(s) created as a result of the transformation.
 */
export type EquivalentProductChecker<
  SelectedItemModel,
  CatalogProductModel extends Pick<CommonProductModel, 'productId'>,
  N,
> = {
  /**
   * Checks whether a given item exists in the new catalog.
   *
   * @param args - The arguments for the check.
   * @param args.item - The product or item being checked.
   * @param args.ctx - The context required for checking the equivalent.
   * @returns `true` if the item exists in the new catalog, otherwise `false`.
   */
  existsInNewCatalog: (args: {
    item: SelectedItemModel;
    ctx: CheckerContext<CatalogProductModel>;
  }) => boolean;

  /**
   * Finds an equivalent item in the new catalog for the given product.
   *
   * @param args - The arguments for the equivalence search.
   * @param args.item - The product or item for which an equivalent is to be found.
   * @param args.ctx - The context required for checking the  equivalent.
   * @returns The equivalent item if found, otherwise `undefined`.
   */
  findEquivalent: (args: {
    item: SelectedItemModel;
    ctx: CheckerContext<CatalogProductModel>;
  }) => SelectedItemModel | undefined;

  /**
   * Builds one or more new design items N[] while keeping the existing product item I unchanged.
   *
   * @param args - Common builder arguments including the current item, context, and any additional data.
   * @returns A list of new design items that retain the current product.
   */
  buildNewDesignItemOnKeepingItem: (
    args: BuilderCommonArgs<SelectedItemModel, CatalogProductModel, N>
  ) => N[];

  /**
   * Builds one or more new design items N[] by replacing the current product item with an equivalent product.
   *
   * @param args - Common builder arguments, including the equivalent product to use as a replacement.
   * @param args.equivalent - The equivalent product item that will replace the original.
   * @returns A list of new design items that include the equivalent product.
   */
  buildNewDesignItemOnReplacingWithEquivalent: (
    args: BuilderCommonArgs<SelectedItemModel, CatalogProductModel, N> & {
      equivalent: SelectedItemModel;
    }
  ) => N[];

  /**
   * Defines how to select a default product item when the original product does not exist
   * in the new catalog and no equivalent can be found.
   *
   * If not defined, the item will be removed instead.
   *
   * You must also define {@link buildNewValueUsingDefault} to specify
   * how to construct new design items from the default item.
   *
   * @param args - The list of arguments.
   * @param args.ctx - The contextual for the catalog to apply the product equivalence.
   * @returns The default item to use when no match or equivalent exists.
   */
  selectDefault?: (args: {
    ctx: CheckerContext<CatalogProductModel>;
  }) => SelectedItemModel;

  /**
   * Defines how to build new design items using a default product item.
   * Used when the product does not exist in the new catalog and no equivalent can be found.
   *
   * If not defined, the item will be removed instead.
   *
   * You must also define {@link selectDefault} to specify
   * how to choose the default item.
   *
   * @param args - Common builder arguments, including the selected default item.
   * @param args.defaultItem - The default product used to create new design items.
   * @returns A list of new design items built from the default item.
   */
  buildNewValueUsingDefault?: (
    args: BuilderCommonArgs<SelectedItemModel, CatalogProductModel, N> & {
      defaultItem: SelectedItemModel;
    }
  ) => N[];

  /**
   * Builds one or more new design items N[] by removing the product item entirely.
   * This is used when no existing, equivalent, or default item is available.
   *
   * @param args - Common builder arguments, including context and item details.
   * @returns A list of new design items reflecting the removal (may be empty).
   */
  buildNewDesignItemOnRemoving?: (
    args: BuilderCommonArgs<SelectedItemModel, CatalogProductModel, N>
  ) => N[];
};

export const EdgeChecker: EquivalentProductChecker<
  EdgeModel,
  EdgeModel,
  DrawingShape
> = {
  existsInNewCatalog: ({ item: edge, ctx: { newCatalog } }) =>
    newCatalog.some(isEqToProductId(edge.productId)),
  findEquivalent: ({ item: edge, ctx: { newCatalog } }) =>
    /** There might be more than one equivalent. We want to find the first given the default sorting for edges */
    selectSortedEdges(newCatalog).find(isEqToMasterSku(edge.masterSku)),
  buildNewDesignItemOnKeepingItem: ({ oldValue }) => oldValue,
  buildNewDesignItemOnReplacingWithEquivalent: ({
    oldValue: clonedDrawingShapes,
    index,
    equivalent,
  }) => {
    clonedDrawingShapes[index].edge = equivalent;

    const newValueAfterReplacements: DrawingShape[] = clonedDrawingShapes.with(
      index,
      clonedDrawingShapes[index]
    );

    return newValueAfterReplacements;
  },
  selectDefault: ({ ctx: { newCatalog } }) => {
    const [firstEdge] = selectSortedEdges(newCatalog);

    return firstEdge;
  },
  buildNewValueUsingDefault: ({
    oldValue: clonedDrawingShapes,
    index,
    defaultItem: edgeDefault,
  }) => {
    /**I leave this lines commented on purpose because it seemed a better solution to create a new instance either with lodash's merge or with a the constructor but both won't work. Causes an error related to losing the class methods.*/
    // const _updatedShape = merge(new DrawingShape(clonedDrawingShapes[index]), clonedDrawingShapes[index], { edge: edgeDefault });
    // const updatedShape = new DrawingShape(_updatedShape); // this ensures the new object is a DrawingShape with all methods See drawing-shape-model cloneDrawingShape
    clonedDrawingShapes[index].edge = edgeDefault;
    const newValueAfterReplacements: DrawingShape[] = clonedDrawingShapes.with(
      index,
      clonedDrawingShapes[index]
    );

    return newValueAfterReplacements;
  },
};

export const TearOutChecker: EquivalentProductChecker<
  SelectedItemWithFullRecord,
  TearOutModel,
  SelectedItemWithFullRecord
> = {
  existsInNewCatalog: ({ item, ctx }) =>
    ctx.newCatalog.some(
      isEqToProductId((item.selectedItem as TearOutModel).productId)
    ),

  findEquivalent: ({ item, ctx }) => {
    const equivalentTearOut = ctx.newCatalog.find(
      isEqToMasterSku(item.selectedItem.masterSku)
    );

    return equivalentTearOut
      ? {
          selectedItem: equivalentTearOut,
          selectedItemProperties: item.selectedItemProperties,
        }
      : undefined;
  },

  buildNewDesignItemOnKeepingItem: ({ oldValue }) => oldValue,

  buildNewDesignItemOnReplacingWithEquivalent: ({
    oldValue,
    index,
    equivalent,
  }) => {
    const newValueAfterReplacements = [...oldValue];

    newValueAfterReplacements[index] = equivalent;

    return newValueAfterReplacements;
  },

  buildNewDesignItemOnRemoving: ({ oldValue, index }) =>
    // Remove the tear-out from the array
    oldValue.filter((_, i) => i !== index),
};

export const CutoutChecker: EquivalentProductChecker<
  SelectedItemWithFullRecord,
  CutoutModel,
  SelectedItemWithFullRecord
> = {
  existsInNewCatalog: ({ item, ctx }) =>
    ctx.newCatalog.some(isEqToProductId(item.selectedItem.productId)),

  findEquivalent: ({ item, ctx }) => {
    const equivalentCutout = ctx.newCatalog.find(
      isEqToMasterSku(item.selectedItem.masterSku)
    );

    return equivalentCutout
      ? {
          selectedItem: equivalentCutout,
          selectedItemProperties: item.selectedItemProperties,
        }
      : undefined;
  },

  buildNewDesignItemOnKeepingItem: ({ oldValue }) => oldValue,

  buildNewDesignItemOnReplacingWithEquivalent: ({
    oldValue,
    index,
    equivalent,
  }) => {
    const newValueAfterReplacements = [...oldValue];

    newValueAfterReplacements[index] = equivalent;

    return newValueAfterReplacements;
  },

  buildNewDesignItemOnRemoving: ({ oldValue, index }) =>
    // Remove the cutout from the array
    oldValue.filter((_, i) => i !== index),
};

export const PlumbingChecker: EquivalentProductChecker<
  SelectedItemWithFullRecord,
  PlumbingProduct,
  SelectedItemWithFullRecord
> = {
  existsInNewCatalog: ({ item, ctx }) =>
    ctx.newCatalog.some(
      isEqToProductId((item.selectedItem as PlumbingProduct).productId)
    ),

  findEquivalent: ({ item, ctx }) => {
    const plumbing = item.selectedItem as PlumbingProduct;

    if (!plumbing.masterSku) {
      return undefined;
    }

    const equivalentPlumbing = ctx.newCatalog.find(
      (_plumbing) => _plumbing.masterSku === plumbing.masterSku
    );

    return equivalentPlumbing
      ? {
          selectedItem: equivalentPlumbing,
          selectedItemProperties: item.selectedItemProperties,
        }
      : undefined;
  },

  buildNewDesignItemOnKeepingItem: ({ oldValue }) => oldValue,

  buildNewDesignItemOnReplacingWithEquivalent: ({
    oldValue,
    index,
    equivalent,
  }) => {
    const newValueAfterReplacements = [...oldValue];

    newValueAfterReplacements[index] = equivalent;

    return newValueAfterReplacements;
  },

  buildNewDesignItemOnRemoving: ({ oldValue, index }) =>
    // Remove the plumbing item from the array
    oldValue.filter((_, i) => i !== index),
};

export const SelectedItemChecker: EquivalentProductChecker<
  SelectedItemWithFullRecord,
  OptionalItem | PlumbingProduct,
  SelectedItemWithFullRecord
> = {
  existsInNewCatalog: ({ item: { selectedItem }, ctx: { newCatalog } }) =>
    newCatalog.some(isEqToProductId(selectedItem.productId)),

  findEquivalent: ({ item, ctx: { newCatalog } }) => {
    const selectedItemEquivalent = newCatalog.find(
      isEqToMasterSku(item.selectedItem.masterSku)
    );

    return selectedItemEquivalent
      ? {
          ...item,
          selectedItem: {
            ...item.selectedItem,
            productId: selectedItemEquivalent.productId,
          },
        }
      : undefined;
  },
  buildNewDesignItemOnKeepingItem: ({ oldValue, item }) => [...oldValue, item],
  buildNewDesignItemOnReplacingWithEquivalent: ({ oldValue, equivalent }) => [
    ...oldValue,
    equivalent,
  ],
};

const isADiagonalCorner = (corner: CornerInfo) => !!corner?.diagonalLength;
const isARadiusCorner = (corner: CornerInfo) => !isADiagonalCorner(corner);

export const CornerChecker: EquivalentProductChecker<
  CornerInfoWithProductInfo,
  CornerRadiusModel,
  DrawingShape
> = {
  existsInNewCatalog: ({ item, ctx: { newCatalog } }) =>
    newCatalog.some(isEqToProductId(item.cornerInfo.productId ?? 0)),
  findEquivalent: ({ item, ctx: { newCatalog } }) => {
    const diagonalEquivalent = isADiagonalCorner(item.cornerInfo)
      ? newCatalog.find(isEqToMasterSku(item.masterSku))
      : undefined;
    // for radius corners, the equivalent is based on range, not sku
    const radiusEquivalent =
      isARadiusCorner(item.cornerInfo) && item.cornerInfo.size
        ? getCornerRadiusFromSize(item.cornerInfo.size, newCatalog)
        : undefined;
    const equivalentProduct = diagonalEquivalent ?? radiusEquivalent;

    if (equivalentProduct) {
      const newCornerInfo = cloneCornerInfo(item.cornerInfo);

      newCornerInfo.productId = equivalentProduct?.productId;

      return { ...item, cornerInfo: newCornerInfo };
    }

    return undefined;
  },
  buildNewDesignItemOnKeepingItem: ({ oldValue }) => oldValue,
  buildNewDesignItemOnReplacingWithEquivalent: ({
    oldValue: clonedDrawingShapes,
    index,
    equivalent,
    item,
  }) => {
    Object.entries(clonedDrawingShapes[index].cornerMap).forEach(
      ([_, cornerInfo]) => {
        if (
          cornerInfo.productId &&
          cornerInfo.productId === item.cornerInfo.productId
        ) {
          cornerInfo.productId = equivalent.cornerInfo.productId;
        }
      }
    );

    const newValueAfterReplacements: DrawingShape[] = clonedDrawingShapes.with(
      index,
      clonedDrawingShapes[index]
    );

    return newValueAfterReplacements;
  },
  buildNewDesignItemOnRemoving: ({
    oldValue: clonedDrawingShapes,
    index,
    item,
  }) => {
    Object.entries(clonedDrawingShapes[index].cornerMap).forEach(
      ([_, cornerInfo]) => {
        if (
          cornerInfo.productId &&
          cornerInfo.productId === item.cornerInfo.productId
        ) {
          cornerInfo.productId = undefined;
          cornerInfo.diagonalLength = undefined;
          cornerInfo.size = DEFAULT_CORNER_RADIUS;
          cornerInfo.label = formatCornerRadiusLabel(cornerInfo.size);
          if (!cornerInfo.labelMeasure?.text) {
            cornerInfo.labelMeasure = EditableLabel.newMeasureText(
              cornerInfo.label,
              cornerInfo.labelMeasure?.fill
            );
          }
          if (!cornerInfo.labelText?.text) {
            cornerInfo.labelText = EditableLabel.newLabelText(cornerInfo.label);
          }
          cornerInfo.labelMeasure.set('text', cornerInfo.label);
          cornerInfo.labelText.set('text', cornerInfo.label);
        }
      }
    );
    const newValueAfterReplacements: DrawingShape[] = clonedDrawingShapes.with(
      index,
      clonedDrawingShapes[index]
    );

    return newValueAfterReplacements;
  },
};
